import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { Mail as MailIcon } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../../Shared/locale';
import { Modal } from '../../../Shared/Modal/Modal';
import { Mail, Product, Project, ProjectType } from '../../../Shared/types';
import { Button, LinkButton } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Icon } from '../../../Shared/UI/Icon';
import { PickerItem } from '../../../Shared/UI/Picker';
import { useRouter } from '../../router';
import { ProjectMailType, SharedProps } from '../../types';
import Container from '../../UI/Container';
import Layout from '../../UI/Layout';
import AddCustomMailForm from './AddCustomMailForm';
import CustomMailCard from './CustomMailCard';
import { ProjectLayout } from './ProjectLayout';

export interface ProjectMailsPageProps extends SharedProps {
  project: Project & {
    new_campaign_mail_enabled: boolean;
    new_campaign_sent_count: number;
    donation_received_sent_count: number;
    donation_confirmation_sent_count: number;
    reminder_mail_enabled: boolean;
    reminder_sent_count: number;
    target_reached_mail_enabled: boolean;
    target_reached_sent_count: number;
    upcoming_event_mail_enabled: boolean;
    upcoming_event_sent_count: number;
    upcoming_event_reminder_mail_enabled: boolean;
    upcoming_event_reminder_sent_count: number;
    mails: (Mail & {
      products: Product[];
      sent_count: number;
      complete: boolean;
    })[];
  };
  projects: PickerItem[];
}

export default function ProjectMailsPage({
  project,
  projects,
  params,
}: ProjectMailsPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();

  const [showModal, setShowModal] = useState(!!params.add_mail);

  return (
    <>
      <Container className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg py-1.5">
            {t('backend:mails')}
          </h3>
          <div className="space-x-2">
            <LinkButton
              href={`${routes.mail_dashboard_page()}?project=${project.id}`}
              variant="secondary"
            >
              {t('backend:dashboard')}
            </LinkButton>
            <Button onClick={() => setShowModal(true)}>
              {t('backend:add_mail')}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <DefaultMailCard project={project} name="new_campaign" />
          <DefaultMailCard project={project} name="donation_confirmation" />
          <DefaultMailCard project={project} name="donation_received" />
          <DefaultMailCard project={project} name="reminder" />
          <DefaultMailCard project={project} name="target_reached" />
          {project.type === ProjectType.Event && (
            <>
              <DefaultMailCard project={project} name="upcoming_event" />
              <DefaultMailCard project={project} name="upcoming_event_reminder" />
            </>
          )}
          {project.mails.map((mail) => (
            <CustomMailCard
              mail={mail}
              project={project}
              key={mail.id}
            />
          ))}
        </div>
      </Container>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="sm">
        <AddCustomMailForm project={project} projects={projects} onSuccess={() => setShowModal(false)} />
      </Modal>
    </>
  );
}

const DefaultMailCard = ({
  project,
  name,
}: {
  project: ProjectMailsPageProps['project'],
  name: ProjectMailType,
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const { routes } = useRouter();

  const enabled = {
    new_campaign: project.new_campaign_mail_enabled,
    donation_confirmation: true,
    donation_received: true,
    reminder: project.reminder_mail_enabled,
    target_reached: project.target_reached_mail_enabled,
    upcoming_event: project.upcoming_event_mail_enabled,
    upcoming_event_reminder: project.upcoming_event_reminder_mail_enabled,
  }[name];

  const muted = !enabled && 'opacity-50';

  return (
    <Card
      spacing="xs"
      link={{
        href: routes.edit_project_mail_page(project.id, name),
        ariaLabel: t(`backend:${name}_mail`),
      }}
    >
      <div className="grid grid-cols-[8fr_2fr_1fr] items-center">
        <div>
          <h4 className={classNames(muted)}>
            <Icon className="mr-1">
              <MailIcon />
            </Icon>
            {t(`backend:${name}_mail`)}
          </h4>
          <div className="text-sm text-slate-400">
            {name === 'donation_confirmation' ? t('backend:default_mail_donor') : t('backend:default_mail')}
          </div>
        </div>
        <div className="text-sm">
          <div className="font-medium">
            {formatNumber(project[`${name}_sent_count`])}×
          </div>
          <div className="text-slate-400">
            {t('backend:sent')}
          </div>
        </div>
        <div
          className={classNames(
            'font-medium text-right',
            enabled ? 'text-emerald-600' : 'text-slate-500'
          )}
        >
          {enabled ? t('backend:active') : t('backend:inactive')}
        </div>
      </div>
    </Card>
  );
};

ProjectMailsPage.layout = (page: ReactNode) => <Layout><ProjectLayout>{page}</ProjectLayout></Layout>;
